import { BlogCard } from './types';

export const paediatricsBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### Allergy Test for Kids in London: What It Involves, How Long It Takes, and How to Book Privately',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },

      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black ',
      },
    },
    description: {
      content:
        'Allergy Test for Kids in London: Quicker, Simpler, and Less Stressful Than You Think',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'allergy-test-for-kids-in-london.webp',
    bannerUrl: 'allergy-test-for-kids-in-london-cover.webp',
    slug: 'what-to-expect-from-an-allergy-test-for-kids',
    contentType: 'article',
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What is an allergy test for kids?',
          id: 'what-is-allergy-test',
        },
        {
          text: 'Signs your child might need an allergy test',
          id: 'signs-allergy-test',
        },
        {
          text: 'What happens during a paediatric allergy test?',
          id: 'what-happens-paediatric-allergy',
        },
        {
          text: 'How to prepare your child before the appointment',
          id: 'how-to-prepare',
        },
        { text: 'How to book a private allergy test', id: 'how-to-book' },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    topics: {
      title: {
        content: '#### TOPICS:',
        styles: {
          h4: 'text-theme-primary font-extrabold p-2',
        },
      },
      topics: [
        'Allergy Test London',
        "Kids' Allergy Test",
        'Paediatric Allergy',
        'Skin Prick Test',
        'Child Allergy Test',
      ],
      styles: {
        p: 'p-2 bg-info-500-opac-10 text-teal-450 bg-info-500-soft',
      },
    },
    speciality: 'Paediatrics',
    date: {
      content: '28, May 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    author: {
      content: 'LIPS Healthcare Marketing Team',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    content: {
      content:
        "### Allergy Test for Kids in London: Quicker, Simpler, and Less Stressful Than You Think\n\nMost parents assume an allergy test for kids means needles, tears, and a difficult hour. In practice, the most common test used for children is a skin prick test. It involves small marks on the forearm, takes around 20 minutes, and most children find it far easier than they expected. If your child keeps having reactions and you want a clear answer without waiting months for an NHS referral, a private allergy test for kids in London can give you results the same day. This guide walks you through exactly what the appointment involves, how to prepare your child, and what happens after.\n\n<span id='what-is-allergy-test'></span>\n\n### What is an allergy test for kids and what does it show?\n\nAn allergy test for kids identifies the specific substances that are triggering your child's immune response. These substances are called allergens. Common allergens in children include foods such as peanuts, tree nuts, milk, eggs, wheat, and soya, as well as environmental triggers such as pollen, dust mites, pet dander, and mould. The test gives you a confirmed clinical answer about which specific things are causing a reaction. That answer helps you and your child's doctor make practical decisions about what to avoid and how to manage symptoms going forward.\n\n<span id='signs-allergy-test'></span>\n\n### What are the signs your child might need an allergy test?\n\nRepeated unexplained reactions are the clearest sign that an allergy test is worth considering. Signs parents commonly notice include skin reactions such as hives, redness, or eczema that flares after eating certain foods, sneezing, a runny nose, or itchy eyes that appear at certain times of year or in certain places, stomach pain, bloating, or vomiting after meals, swelling around the lips or face after eating, and a persistent cough or change in breathing that does not clear up with standard treatment.\n\nIf your child has had any reaction involving significant swelling, breathing difficulty, or required emergency attention, do not wait for a routine appointment. Seek a clinical assessment promptly.\n\n<strong><a href=\"/paediatric-clinic#booking-calendar-widget\" target=\"_blank\" rel=\"noopener noreferrer\">Book a private allergy test</a></strong>\n\n<span id='what-happens-paediatric-allergy'></span>\n\n### What actually happens during a paediatric allergy test?\n\nA paediatric allergy test is far less daunting than most parents imagine, and knowing what to expect makes a real difference on the day.\n\nThe most common method used for children is a skin prick test. A small amount of allergen extract is placed on your child's forearm. A tiny lancet then makes a light, shallow contact with the skin underneath it. This is not a needle going in. Most children describe it as feeling like a brief scratch. Several allergens can be tested at the same time, appearing as a row of small marks along the forearm. After around 15 to 20 minutes, the clinician checks the skin's response. A small raised area at any of the test sites indicates a reaction to that allergen. Results are available immediately, before you leave the room.\n\nIn some cases, a blood test is used instead. A small blood sample is taken and sent to a laboratory to measure allergy antibodies. This approach is used when a skin prick test is not the right fit for your child, for example if they have a skin condition or are taking certain medications. The clinician will explain which method is appropriate for your child and why, before anything starts.\n\n[LIPS Healthcare](/paediatric-clinic#booking-calendar-widget), you will know exactly what to expect before the test begins. Nothing happens without it being explained to you and your child first.\n\n<span id='how-to-prepare'></span>\n\n### How to prepare your child before the appointment?\n\nA little preparation goes a long way in making the appointment calm and straightforward.\n\nAntihistamines need to be paused before a skin prick test because they can suppress the skin's response and affect the accuracy of the results. Your clinician will tell you how many days in advance to stop them when you book. Do not stop any other medication without checking first.\n\nTell your child what to expect in simple, honest terms. Let them know the appointment involves small marks on their arm that may feel like a light scratch, that it does not take long, and that you will be there with them the whole time. Children who know what is coming are almost always calmer than children who do not. If a skin prick test is being used, avoid the word needle as it is not an injection and the word alone can create unnecessary worry.\n\nDress your child in a short-sleeved top or something with sleeves that roll up easily. Bring a small snack for after the appointment as a reward. Most children are fine to eat normally beforehand unless the clinician advises differently.\n\n<span id='how-to-book'></span>\n\n### How to book a private allergy test for your child in London?\n\nBooking a private allergy test for kids at LIPS Healthcare requires no GP referral and no prior registration. You book directly through [paediatrics](/paediatric-clinic#booking-calendar-widget) or by calling the clinic. Same day and next-day appointments are available depending on the schedule.\n\nWhen you book, bring a short note of your child's symptoms, when they started, and anything you have noticed that seems to trigger them. You do not need a formal medical history. A few clear sentences about what you have observed is enough to help the clinician plan the appointment well.\n\n[LIPS Healthcare](/paediatric-clinic#booking-calendar-widget) is at Battersea Power Station, London SW8. Easy to reach from Chelsea, Clapham, Vauxhall, Pimlico, and Wandsworth via the Northern line (Battersea Power Station station), Overground, bus, or cycle routes.",
      styles: {
        h3: 'text-lg md:text-xl font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action ',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions (FAQS)',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'What is the best allergy test for a child?',
          },
          answer: {
            content:
              'Skin prick testing is the most widely used method for children. It is quick, produces immediate results, and most children tolerate it well. The clinician will advise which method suits your child based on their age, skin condition, and symptoms.',
          },
        },
        {
          question: { content: 'How long does a kids allergy test take?' },
          answer: {
            content:
              'Around an hour in total. The skin prick test itself takes 20 to 30 minutes including the waiting period. Allow extra time for the consultation beforehand and the results discussion afterwards.',
          },
        },
        {
          question: { content: 'Does an allergy test hurt for kids?' },
          answer: {
            content:
              'Not significantly. A skin prick test involves a light scratch on the forearm, not a needle injection. Most children find it much less uncomfortable than a standard blood test, and many are surprised by how quick it is.',
          },
        },
        {
          question: {
            content: 'What happens after the results come back?',
          },
          answer: {
            content:
              "The clinician talks you through the results in plain terms during the same appointment. They explain which allergens showed a response and what the practical next steps are. You receive a written record of the results to keep and share with your child's GP if needed.",
          },
        },
        {
          question: {
            content: 'How do I prepare my child for an allergy test?',
          },
          answer: {
            content:
              'Stop antihistamines as advised before the appointment. Dress your child in a short-sleeved top. Explain to them in simple terms that the test involves a light scratch on their arm and that you will be right there with them. Children who know what is coming cope far better on the day.',
          },
        },
      ],
    },
    postFaqContent: {
      content:
        '<strong><a href="/paediatric-clinic#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Book a private allergy test</a></strong> \n\n > ⓘ Medical disclaimer: This article is for informational purposes only and does not constitute medical advice. If your child has experienced a severe allergic reaction involving swelling, breathing difficulty, or loss of consciousness, call 999 immediately. For urgent but non-emergency concerns, call 111.\n\nSource : [NHS guidance on allergies](/paediatric-clinic#booking-calendar-widget)',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'inline-block text-theme-primary font-bold no-underline text-center',
        blockquote:
          'border-l-4 border-theme-primary bg-surface-secondary border border-blue-200 rounded-lg p-4 my-8 text-sm text-text-default not-italic',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer mt-8 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
      },
    },
  },
];
